import { useCallback, RefObject, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { CellValueChangedEvent } from 'ag-grid-community';
import { getItemPopupList } from "src/apis/itemApi";
import { restoreOriginalValues } from "../Util_KSK2011_R01";
import { KSK2011_R01Dto } from "../Dto_KSK2011_R01";
import { ALLOWED_ITEM_ACCOUNTS } from "src/components/common/common";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { getEmployeeList } from "src/apis/employeeApi";
import { focusToCell } from "src/utils/grid/GridHandlerContent";
import { useDetailTableHandlers } from "src/hooks/useDetailTableHandlers";
import { DetailTableFieldConfig } from "src/types/detail-table";
import { CodeSearchConfig as CodeSearchConfigType } from "src/types/code-search";

interface UseKSK2011_R01CellChangeProps {
	KSK2011_R01List: KSK2011_R01Dto[];
	updateKSK2011_R01List: (index: number, data: KSK2011_R01Dto) => void;
	originalKSK2011_R01List: KSK2011_R01Dto[];
	settingCodeSearchModalProps: (
		category: string,
		search: string,
		isInfo: boolean,
		handleFoucsId: string,
		originalData: KSK2011_R01Dto,
		valueIndex: number,
		extraFilter?: { itemAccount?: string }
	) => void;
	gridRef: RefObject<AgGridReact<KSK2011_R01Dto> | null>;
}

export function useKSK2011_R01CellChange({
	KSK2011_R01List,
	updateKSK2011_R01List,
	originalKSK2011_R01List,
	settingCodeSearchModalProps,
	gridRef
}: UseKSK2011_R01CellChangeProps) {

	const { workPlaceList } = useCommonCodeStore();

	// fieldConfig 설정 (useDetailTableHandlers용)
	const fieldConfig: DetailTableFieldConfig = useMemo(() => ({
		qtyField: 'orderQty',
		priceField: 'orderPrice', // KSK2011_R01에는 없지만 필수 필드
		unitField: 'itemUnit',
		codeSearchFields: {
			itemCd: {
				codeField: 'itemCd',
				category: '품목',
				responseMapping: {
					itemCd: 'itemCd',
					itemNm: 'itemNm',
					itemSpec: 'itemSpec',
					itemUnit: 'itemUnit'
				},
				clearFields: {
					itemCd: '',
					itemNm: '',
					itemSpec: '',
					itemUnit: ''
				},
				nextFocusCell: 'orderQty', // 품목 검색 성공 시 수량으로 포커스 이동
				searchFunction: async (code: string, onFound: (item: any) => void) => {
					try {
						const response = await getItemPopupList(code, ALLOWED_ITEM_ACCOUNTS, '', '');
						const found = response.data.data.find((item: { itemCd: string }) => item.itemCd === code);
						if (found) {
							onFound(found);
							return found;
						}
						return null;
					} catch (error) {
						console.error('품목 검색 중 오류:', error);
						return null;
					}
				}
			},
			orderEmp: {
				codeField: 'orderEmp',
				category: '작업자',
				responseMapping: {
					empNo: 'orderEmp',
					empNm: 'orderEmpNm'
				},
				clearFields: {
					orderEmp: '',
					orderEmpNm: ''
				},
				nextFocusCell: 'itemCd', // 작업자 검색 성공 시 품목코드로 포커스 이동
				searchFunction: async (code: string, onFound: (item: any) => void) => {
					try {
						const response = await getEmployeeList(code);
						const found = response.data.data.find((employee: { empNo: string }) => employee.empNo === code);
						if (found) {
							onFound(found);
							return found;
						}
						return null;
					} catch (error) {
						console.error('작업자 검색 중 오류:', error);
						return null;
					}
				}
			}
		}
	}), []);

	// codeSearchConfig 설정 (useDetailTableHandlers용)
	const codeSearchConfig: CodeSearchConfigType = useMemo(() => ({
		formName: 'KSK2011_R01',
		useLegacyFocus: false // 포커스 이동은 fieldConfig의 nextFocusCell에서 처리
	}), []);

	// calculateAmount 함수 (KSK2011_R01에는 금액 계산이 없으므로 단순 반환)
	const calculateAmount = useCallback((data: KSK2011_R01Dto): KSK2011_R01Dto => {
		return data; // KSK2011_R01에는 금액 계산이 없음
	}, []);

	// useDetailTableHandlers 사용 (공통 코드 검색 로직)
	const commonHandlers = useDetailTableHandlers<KSK2011_R01Dto>({
		detailList: KSK2011_R01List,
		updateDetailList: updateKSK2011_R01List,
		originalDetailList: originalKSK2011_R01List,
		setCodeSearchModalProps: (props: any) => {
			// KSK2011_R01의 settingCodeSearchModalProps로 변환
			settingCodeSearchModalProps(
				props.category,
				props.search,
				props.isInfo || false,
				props.handleFoucsId || props.handleFocusId,
				props.originalData || props.rowData,
				props.valueIndex || props.rowIndex,
				props.extraFilter
			);
		},
		gridRef: gridRef as any,
		fieldConfig,
		codeSearchConfig,
		calculateAmount
	});

	// KSK2011_R01 특화 셀 변경 핸들러 (하이브리드 접근)
	const handleCellChange = useCallback(
		async (event: CellValueChangedEvent<KSK2011_R01Dto>) => {
			const { data, colDef, newValue, oldValue } = event;
			if (!data || !colDef.field)	return;
			const field = colDef.field as keyof KSK2011_R01Dto;

			// KSK2011_R01 특화 로직: compCd 변경 시 workplaceCd 초기화
			if (field === 'compCd') {
				data.workplaceCd = '';
			}

			// KSK2011_R01 특화 로직: workplaceCd 변경 시 workProcess 자동 설정
			if (field === 'workplaceCd') {
				const selectedWorkplaceCd = data.workplaceCd;
				const workPlaceListToUse = workPlaceList.filter(opt => opt.useYn === 'Y' && opt.orderYn === 'Y');
				const workPlace = workPlaceListToUse.find(
					(opt) => opt.workplaceCd === selectedWorkplaceCd
				);
				data.workProcess = workPlace ? workPlace.workProcess : '';
			}

			// 공통 코드 검색 로직은 useDetailTableHandlers 사용
			const codeFieldConfig = fieldConfig.codeSearchFields?.[field as string];
			if (codeFieldConfig && newValue !== oldValue) {
				await commonHandlers.handleCellChange(event);
				return;
			}

			// 기타 필드 변경 처리
			updateKSK2011_R01List(data.valueIndex!, {
				...data,
				[field]: newValue,
				isModified: true
			});
		},
		[
			commonHandlers,
			fieldConfig,
			updateKSK2011_R01List,
			workPlaceList
		]
	);

	// 하위 호환성을 위한 래퍼 함수들 (기존 코드와의 호환성 유지)
	// useDetailTableHandlers의 handleCellChange를 시뮬레이션하여 코드 검색 처리
	const handleItemCodeSearch = useCallback(
		async (itemCode: string, rowData: KSK2011_R01Dto, oldValue?: string) => {
			const codeFieldConfig = fieldConfig.codeSearchFields?.itemCd;
			if (!codeFieldConfig) return;

			// useDetailTableHandlers의 handleCellChange를 시뮬레이션
			const mockEvent = {
				data: rowData,
				colDef: { field: 'itemCd' },
				newValue: itemCode,
				oldValue: oldValue
			} as CellValueChangedEvent<KSK2011_R01Dto>;

			await commonHandlers.handleCellChange(mockEvent);
		},
		[commonHandlers, fieldConfig]
	);

	const handleOrderEmpSearch = useCallback(
		async (orderEmp: string, rowData: KSK2011_R01Dto, oldValue?: string) => {
			const codeFieldConfig = fieldConfig.codeSearchFields?.orderEmp;
			if (!codeFieldConfig) return;

			// useDetailTableHandlers의 handleCellChange를 시뮬레이션
			const mockEvent = {
				data: rowData,
				colDef: { field: 'orderEmp' },
				newValue: orderEmp,
				oldValue: oldValue
			} as CellValueChangedEvent<KSK2011_R01Dto>;

			await commonHandlers.handleCellChange(mockEvent);
		},
		[commonHandlers, fieldConfig]
	);

	return {
		handleCellChange,
		handleItemCodeSearch,
		handleOrderEmpSearch
	};
}
